import type { QueryClient } from '@tanstack/react-query';
import type {
  ClientOrderChatMessage,
  ClientOrderChatMessagesResponse,
  ClientOrderChatSessionsResponse,
} from './types';

export const ORDER_CHAT_SESSIONS_QUERY_KEY = ['client-order-chat', 'sessions'] as const;
export const ORDER_CHAT_UNREAD_QUERY_KEY = ['client-order-chat', 'unread-count'] as const;

export function orderChatMessagesQueryKey(problemMessageId: number) {
  return ['client-order-chat', 'messages', problemMessageId] as const;
}

export function mergeOrderChatMessages(
  prev: ClientOrderChatMessagesResponse | undefined,
  next: ClientOrderChatMessagesResponse,
): ClientOrderChatMessagesResponse {
  if (!prev?.items?.length) return next;
  const byId = new Map<number, ClientOrderChatMessage>();
  for (const m of prev.items) byId.set(m.id, m);
  for (const m of next.items) byId.set(m.id, m);
  const items = Array.from(byId.values()).sort((a, b) => a.id - b.id);
  return {
    ...next,
    items,
    total: Math.max(next.total, items.length),
  };
}

export function appendOrderChatMessageToCache(
  queryClient: QueryClient,
  problemMessageId: number,
  message: ClientOrderChatMessage,
  opts?: { isActive?: boolean },
) {
  queryClient.setQueryData<ClientOrderChatMessagesResponse>(
    orderChatMessagesQueryKey(problemMessageId),
    (prev) => {
      if (!prev) {
        return {
          items: [message],
          total: 1,
          threadId: message.threadId,
          problemMessageId,
          resolved: false,
        };
      }
      if (prev.items.some((m) => m.id === message.id)) return prev;
      return {
        ...prev,
        items: [...prev.items, message],
        total: prev.total + 1,
      };
    },
  );

  queryClient.setQueryData<ClientOrderChatSessionsResponse>(
    ORDER_CHAT_SESSIONS_QUERY_KEY,
    (prev) => {
      if (!prev) return prev;
      const existing = prev.items.find(
        (session) => session.problemMessageId === problemMessageId,
      );
      if (!existing) return prev;
      const unreadDelta =
        opts?.isActive || message.senderKind === 'SELLER' ? 0 : 1;
      const updated = {
        ...existing,
        lastMessage: message,
        updatedAt: message.createdAt,
        unreadCount: existing.unreadCount + unreadDelta,
        resolved:
          message.systemEvent === 'ORDER_PROBLEM_RESOLVED'
            ? true
            : existing.resolved,
      };
      const without = prev.items.filter(
        (session) => session.problemMessageId !== problemMessageId,
      );
      return { ...prev, items: [updated, ...without] };
    },
  );
}
